import type { PracticeDayHours, PracticeOfficeHours } from '@/hooks/practice/practiceApi';

export const PRACTICE_DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type PracticeDayKey = (typeof PRACTICE_DAY_KEYS)[number];

export const CLOSED_HOURS = 'Closed';

/**
 * The trailing `Z` on Salesforce's `HH:mm:ss.SSSZ` is a serialization artifact, not a UTC offset —
 * the value is already wall-clock time at the practice. Parsing the string as a date would shift
 * every time by the viewer's offset, so the parts are read straight off it.
 */
function toParts(value: string): { hours: number; minutes: number } | null {
  const match = /^(\d{2}):(\d{2})/.exec(value);
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return { hours, minutes };
}

/**
 * Formats against a fixed instant in UTC rather than local time: UTC has no DST, so the hour and
 * minute always round-trip unchanged while `Intl` still picks the locale's 12/24-hour convention
 * and its own AM/PM equivalent. `format` takes a timestamp, so no `Date` is ever built.
 */
function timeFormatter(locale: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
}

const REFERENCE_DAY = [2000, 0, 1] as const;

function formatSpan(day: PracticeDayHours, format: Intl.DateTimeFormat): string {
  const start = day.start ? toParts(day.start) : null;
  const end = day.end ? toParts(day.end) : null;
  if (!start || !end) {
    return CLOSED_HOURS;
  }
  const at = (p: { hours: number; minutes: number }) => format.format(Date.UTC(...REFERENCE_DAY, p.hours, p.minutes));
  return `${at(start)} – ${at(end)}`;
}

export function formatDayHours(day: PracticeDayHours, locale: string): string {
  return formatSpan(day, timeFormatter(locale));
}

export interface OfficeHoursRow {
  from: PracticeDayKey;
  to: PracticeDayKey;
  hours: string;
  /** Lets the caller swap in a translated label — `hours` carries the untranslated `Closed`. */
  closed: boolean;
}

/** Consecutive days sharing the same hours collapse into one `Mon – Thu` row, per the design. */
export function groupOfficeHours(officeHours: PracticeOfficeHours, locale: string): OfficeHoursRow[] {
  const format = timeFormatter(locale);
  const rows: OfficeHoursRow[] = [];
  for (const key of PRACTICE_DAY_KEYS) {
    const hours = formatSpan(officeHours[key], format);
    const previous = rows[rows.length - 1];
    if (previous && previous.hours === hours) {
      previous.to = key;
      continue;
    }
    rows.push({ from: key, to: key, hours, closed: hours === CLOSED_HOURS });
  }
  return rows;
}
